import { Flex, IconButton, Text, Tooltip, useTheme } from '@chakra-ui/react';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { withTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { setIsBannerShown } from '../../app-state/slices/AppDataSlice';
import {
    removeUser,
    setSelectedUser,
} from '../../app-state/slices/ClientsSlice';
import { RootState } from '../../app-state/store';
import CanopyManagerBanner from '../../canopy-ui-components/components/CanopyManagerBanner/CanopyManagerBanner';
import ActAddcl from '../../canopy-ui-components/components/Icons/ActAddcl';
import NavBar, {
    getNavBarMaxUsers,
} from '../../canopy-ui-components/components/Navbar/Navbar';
import Users from '../../canopy-ui-components/components/Navbar/Users';
import Sider from '../../canopy-ui-components/components/Sider/Sider';
import Tags from '../../canopy-ui-components/components/Tags/Tags';
import { useCurrentColors } from '../../canopy-ui-components/hooks/UseCurrentColors/UseCurrentColors';
import { useCheckForMobileOrTablet } from '../../canopy-ui-components/hooks/UseResponsive/UseResponsive';
import appConfig from '../../config/app';
import PreferencesModal from '../Preferences/PreferencesModal';
import UserInactivity from '../Session/UserInactivity';
import PagesWithTabs from './PagesWithTabs';
import PagesWithoutTabs from './PagesWithoutTabs';

function AppWrapper({
    children,
    t,
    selectedMenu,
    type,
}: {
    children?: any;
    t: any;
    selectedMenu: any;
    type?: ClientsTypes;
}) {
    const [isNavBarExpanded, setIsNavBarExpanded] = useState(false);
    const currentColors = useCurrentColors();
    const dispatch = useDispatch();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const router = useNavigate();
    const isMobile = useCheckForMobileOrTablet();
    const [totalItems, setTotalItems] = useState<any[]>([]);
    const navBarMaxUsers = getNavBarMaxUsers(useCheckForMobileOrTablet());
    const theme = useTheme();
    const {
        appData: {
            menuStructure: {
                data: { sider, navBar, pagesWithSubmenu, ...submenus },
            },
            isBannerShown,
        },
        clients,
        auth: { isSessionValid },
    } = useSelector((state: RootState) => state);

    useEffect(() => {
        if (isSessionValid) {
            if (isBannerShown === null) {
                dispatch(
                    // setIsBannerShown(
                    //     theme?.isMainline &&
                    //         !!appConfig?.basePath &&
                    //         navBar?.find(
                    //             (item: MenuType) => item.key === 'current-viz'
                    //         )
                    // )
                    setIsBannerShown(false)
                );
            }
        }
    }, [isSessionValid, isBannerShown]);

    const mGroups: PortfolioUser[] = [];

    useEffect(() => {
        let items: any[] = [];
        if (type) {
            clients[type]?.groups?.forEach((group) => {
                if (group?.users?.length > 0) {
                    mGroups.push({
                        uniqueId: group.uniqueId,
                        id: group.users?.map((u) => u.id).join(','),
                        meta: group.users?.map((u) => u.displayName).join(','),
                        username: group.name,
                        displayName: group.name,
                        users: group.users,
                        type: 'GROUPED_CLIENT',
                    });
                }
            });
            items = clients[type]?.users?.concat(mGroups) || [];
            setTotalItems(items);
        }
    }, [type, clients]);

    return (
        <UserInactivity t={t}>
            <Flex>
                <>
                    <Sider
                        isOpen={sidebarOpen}
                        setIsOpen={setSidebarOpen}
                        menu={sider}
                    />
                    <Flex
                        zIndex={3}
                        width={isMobile ? '100%' : 'calc(100% - 120px)'}
                        direction="column"
                        position="relative"
                        marginLeft={isMobile ? '0' : '100px'}
                        marginRight={isMobile ? '0' : '20px'}>
                        {
                            <CanopyManagerBanner
                                t={t}
                                isBannerShown={isBannerShown}
                            />
                        }
                        <NavBar
                            type={type}
                            selectedMenu={selectedMenu}
                            setIsNavBarExpanded={setIsNavBarExpanded}
                            setSidebarOpen={setSidebarOpen}
                            menu={navBar}
                            title={
                                selectedMenu !== 'HTTP 404'
                                    ? t(`ui.mm.page-title-${selectedMenu}`)
                                    : selectedMenu
                            }
                            expansionContent={
                                totalItems?.length > navBarMaxUsers && (
                                    <Flex
                                        width="100%"
                                        alignItems="center"
                                        justifyContent="space-between">
                                        <Text
                                            color={
                                                currentColors.navbarTextColor
                                            }
                                            fontWeight="bold"
                                            fontSize="15px">
                                            {t('portfolio-list')}
                                        </Text>
                                        <Tags
                                            flexGrow={1}
                                            overflowX="auto"
                                            mx="10px"
                                            items={totalItems.slice(
                                                navBarMaxUsers,
                                                totalItems?.length + 1
                                            )}
                                            selected={
                                                type
                                                    ? clients[type].selectedUser
                                                    : null
                                            }
                                            uniqueId={'uniqueId'}
                                            dataKey="displayName"
                                            tooltipKey="meta"
                                            onClick={(user) => {
                                                dispatch(
                                                    setSelectedUser({
                                                        user,
                                                        type,
                                                    })
                                                );
                                            }}
                                            onDelete={(user) => {
                                                dispatch(
                                                    removeUser({
                                                        user,
                                                        type,
                                                    })
                                                );
                                            }}
                                        />
                                        <Tooltip
                                            aria-label="add-user"
                                            label={t(
                                                'ui.mm.tooltip.add-client'
                                            )}
                                            placement="bottom-start"
                                            hasArrow>
                                            <IconButton
                                                background="transparent"
                                                cursor="pointer"
                                                border="none"
                                                icon={<ActAddcl />}
                                                variant="ghost"
                                                aria-label="add-user"
                                                height="26px !important"
                                                minWidth="26px !important"
                                                width="26px !important"
                                                color={
                                                    currentColors.contentText
                                                }
                                                onClick={() => {
                                                    router(`/${selectedMenu}`);
                                                }}
                                                _hover={{
                                                    backgroundColor:
                                                        'transparent',
                                                }}
                                                _focus={{
                                                    border: '0px',
                                                    backgroundColor:
                                                        'transparent',
                                                    outline: 'none',
                                                }}
                                                _active={{ border: '0px' }}
                                            />
                                        </Tooltip>
                                    </Flex>
                                )
                            }
                            isBannerShown={isBannerShown}
                            t={t}>
                            {type && (
                                <Users
                                    {...clients[type]}
                                    selectedMenu={selectedMenu}
                                    onSelectUser={(user: any) => {
                                        dispatch(
                                            setSelectedUser({ user, type })
                                        );
                                    }}
                                    onDeleteUser={(user: any) => {
                                        dispatch(removeUser({ user, type }));
                                    }}
                                    onAddUser={() => {
                                        router('/portfolio/');
                                    }}
                                    t={t}
                                />
                            )}
                        </NavBar>
                        {pagesWithSubmenu.includes(selectedMenu) ? (
                            <PagesWithTabs
                                isNavBarExpanded={isNavBarExpanded}
                                tabs={submenus?.[selectedMenu]}
                                isBannerShown={isBannerShown}>
                                {React.cloneElement(children, { t })}
                            </PagesWithTabs>
                        ) : (
                            <PagesWithoutTabs
                                isNavBarExpanded={isNavBarExpanded}
                                isBannerShown={isBannerShown}>
                                {React.cloneElement(children, { t })}
                            </PagesWithoutTabs>
                        )}
                    </Flex>
                    {navBar?.find(({ key }) => key === 'settings') ? (
                        <PreferencesModal t={t} />
                    ) : null}
                </>
            </Flex>
        </UserInactivity>
    );
}

AppWrapper.propTypes = {
    t: PropTypes.func.isRequired,
};
export default withTranslation('translations')(AppWrapper);
